// A yasna sense's first entry, usually its meaning ("sheep"); its short
// gloss otherwise. The student page shows it on each word chip:
// "mouton » sheep" (Requirement 52). Same as scripts/export-learner-data.mjs.
export function meaningOf(entry) {
  if (!entry) return null;
  const first = Array.isArray(entry.cards) ? entry.cards[0] : null;
  if (first && typeof first.value === 'string' && first.value.trim()) return first.value.trim();
  return (entry.short_gloss || '').trim() || null;
}

// A sense's headword: the article for nouns, and the gender in brackets
// where the article hides it — "le mouton", "l'eau (f.)", "les adultes
// (m./f.)". The same rule as yasna's src/lib/headword.js (its embed's
// headword()); kept here for the snapshot exporter and as a fallback.
const GENDER_LABEL = { m: 'm.', f: 'f.', mf: 'm./f.' };
const PLURAL_ARTICLES = { fr: ['les'] };

export function genderOf(entry) {
  if (!entry) return null;
  if (GENDER_LABEL[entry.gender]) return entry.gender;
  const tags = (entry.cards || [])
    .filter((c) => c && c.card_type === 'tag' && typeof c.label === 'string')
    .map((c) => c.label.toLowerCase());
  const m = tags.includes('masculine');
  const f = tags.includes('feminine');
  if (m && f) return 'mf';
  if (m) return 'm';
  return f ? 'f' : null;
}

export function headwordOf(entry) {
  if (!entry || !entry.word) return null;
  const { article, word, language } = entry;
  const base = article ? `${article}${article.endsWith("'") ? '' : ' '}${word}` : word;
  const label = GENDER_LABEL[genderOf(entry)];
  const hides = !!article && (article.endsWith("'") || (PLURAL_ARTICLES[language] || []).includes(article.toLowerCase()));
  return label && hides ? `${base} (${label})` : base;
}

// What a sense says literally, for the word-for-word line (Requirement 61).
// The same rule as yasna's src/lib/wordForWord.js (its embed's
// wordForWord() / literalRoles()); kept here for the snapshot exporter and
// as a fallback. A single word's "word for word" card has one row per
// grammatical role ({ meaning: gloss, note: role }); a multi-word entry's
// "literal meaning" / "made up of" card glosses each part.
const WORD_FOR_WORD = 'word for word';
const PART_LABELS = ['literal meaning', 'made up of'];
const clean = (s) => (typeof s === 'string' ? s.trim() : '');

// [{ role, gloss }]: the roles a word can play, each with its gloss.
export function literalRolesOf(entry) {
  const card = ((entry && entry.cards) || []).find((c) => c && c.label === WORD_FOR_WORD && Array.isArray(c.entries));
  if (!card) return [];
  return card.entries
    .filter((e) => e && clean(e.meaning))
    .map((e) => ({ role: clean(e.note) || null, gloss: clean(e.meaning) }));
}

// [{ word, gloss }] for this role (else the first), or null.
export function literalOf(entry, role) {
  if (!entry) return null;
  const rows = literalRolesOf(entry);
  if (rows.length) {
    const wanted = clean(role).toLowerCase();
    const row = (wanted && rows.find((r) => (r.role || '').toLowerCase() === wanted)) || rows[0];
    return [{ word: entry.word, gloss: row.gloss }];
  }
  for (const label of PART_LABELS) {
    const card = (entry.cards || []).find((c) => c && c.label === label && Array.isArray(c.entries));
    const parts = card ? card.entries.filter((e) => e && clean(e.word) && clean(e.meaning)) : [];
    if (parts.length) return parts.map((e) => ({ word: clean(e.word), gloss: clean(e.meaning) }));
  }
  return null;
}

// yasna's own rule when the loaded embed offers it, else the copy above.
export function literalWith(yasna, entry, role) {
  if (yasna && typeof yasna.wordForWord === 'function') return yasna.wordForWord(entry, role || undefined);
  return literalOf(entry, role);
}

export function rolesWith(yasna, entry) {
  if (yasna && typeof yasna.literalRoles === 'function') return yasna.literalRoles(entry);
  return literalRolesOf(entry);
}
